import { OktaAuth } from '@okta/okta-auth-js';

const isLocal = window.location.href.indexOf('localhost') >= 0;

// Okta Configuration - Use environment variables for the new Okta app
const oktaAuth = new OktaAuth({
    issuer: process.env.VUE_APP_OKTA_ISSUER || "https://codeway.okta.com/oauth2/ausyx5t19iv196XOc697",
    clientId: process.env.VUE_APP_OKTA_CLIENT_ID || "0oaywyriesk40LM1U697",
    redirectUri: window.location.origin + '/login/callback',
    postLogoutRedirectUri: isLocal ? "http://localhost:5002" : window.location.origin,
    scopes: ['openid', 'profile', 'email', 'groups', 'offline_access'],
    responseType: 'code',
    pkce: true,
    // okta-auth-js 8 no longer decodes access tokens by default; getUserGroups() reads claims.
    decodeAccessTokens: true,
    tokenManager: {
        storage: 'localStorage',
        autoRenew: false,
        autoRemove: false,
        secure: !isLocal,
        expireEarlySeconds: 120,
    },
    services: {
        autoRenew: false
    },
    ...(isLocal && {
        disableHttpsCheck: true,
    }),
});

const tokenManagerService = {
    _idleInterval: null,
    _lastActivity: Date.now(),
    _maxIdleTime: 8 * 60 * 60 * 1000, // 8 hours
    _isHandlingAuthFailure: false,
    _tokenRenewalTimeout: null,

    /**
     * Initialize the service
     */
    async initialize() {
        try {
            oktaAuth.tokenManager.start();
            this._setupTokenEventListeners();
            this._startTokenExpirationListener();
            this._startIdleMonitoring();
            console.log('✅ Auth Service initialized');
        } catch (error) {
            console.error('❌ Auth Service init failed:', error);
        }
    },

    /**
     * Setup event listeners for token expiration and errors
     */
    _setupTokenEventListeners() {
        oktaAuth.tokenManager.on('renewed', (key) => {
            console.log(`✅ Token renewed successfully: ${key}`);
        });

        oktaAuth.tokenManager.on('expired', (key) => {
            console.warn(`🔐 Token expired: ${key}`);
            console.warn('⚠️ This should not happen if autoRenew is working correctly.');
        });

        oktaAuth.tokenManager.on('error', (error) => {
            console.error('❌ Token manager error:', error);
            
            if (error && (error.error === 'invalid_grant' || error.error === 'login_required')) {
                console.log('🔒 Refresh token invalid - redirecting to login');
                this.handleAuthFailure('Token renewal failed - refresh token invalid');
            } else {
                console.warn('⚠️ Token manager error (non-fatal):', error.error || error.message);
            }
        });
    },

    _startTokenExpirationListener() {
        this._stopTokenExpirationListener();
        this._scheduleTokenRenewal();
    },

    _stopTokenExpirationListener() {
        if (this._tokenRenewalTimeout) {
            clearTimeout(this._tokenRenewalTimeout);
            this._tokenRenewalTimeout = null;
        }
    },

    async _scheduleTokenRenewal() {
        try {
            const accessToken = await oktaAuth.tokenManager.get('accessToken');
            const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
            
            if (!accessToken?.expiresAt) {
                this._tokenRenewalTimeout = setTimeout(() => this._scheduleTokenRenewal(), 60000);
                return;
            }

            const now = Math.floor(Date.now() / 1000);
            
            if (refreshToken?.expiresAt && refreshToken.expiresAt < accessToken.expiresAt) {
                const refreshRenewalTime = refreshToken.expiresAt - now - 120;
                if (refreshRenewalTime <= 0) {
                    await this._renewTokenBeforeExpiry();
                    return;
                }
                const accessRenewalTime = accessToken.expiresAt - now - 120;
                const renewalTime = Math.min(refreshRenewalTime, accessRenewalTime);
                this._tokenRenewalTimeout = setTimeout(() => this._renewTokenBeforeExpiry(), renewalTime * 1000);
                return;
            }

            const renewalTime = accessToken.expiresAt - now - 120;
            if (renewalTime <= 0) {
                await this._renewTokenBeforeExpiry();
            } else {
                this._tokenRenewalTimeout = setTimeout(() => this._renewTokenBeforeExpiry(), renewalTime * 1000);
            }
        } catch (error) {
            console.error('❌ Error scheduling token renewal:', error);
            this._tokenRenewalTimeout = setTimeout(() => this._scheduleTokenRenewal(), 60000);
        }
    },

    async _renewTokenBeforeExpiry() {
        const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
        const now = Math.floor(Date.now() / 1000);

        if (!refreshToken || refreshToken.expiresAt <= now || refreshToken.expiresAt < (now + 120)) {
            this.handleAuthFailure('Token renewal failed - refresh token expired');
            return;
        }

        try {
            await oktaAuth.tokenManager.renew('accessToken');
            this.updateActivity();
            this._scheduleTokenRenewal();
        } catch (error) {
            if (error?.error === 'invalid_grant' || error?.error === 'login_required') {
                this.handleAuthFailure('Token renewal failed - refresh token invalid');
            } else {
                this._scheduleTokenRenewal();
            }
        }
    },

    /**
     * Centralized handler for authentication failures
     */
    handleAuthFailure(reason = 'Authentication required') {
        if (this._isHandlingAuthFailure) {
            return;
        }
        this._isHandlingAuthFailure = true;

        console.log(`🔒 Auth failure: ${reason}. Redirecting to login...`);

        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && !['/signin', '/login/callback'].includes(currentPath)) {
            if (!sessionStorage.getItem('okta_redirect_path')) {
                sessionStorage.setItem('okta_redirect_path', currentPath);
            }
        }

        this.clearTokenCache();
        this._stopIdleMonitoring();
        this._stopTokenExpirationListener();

        if (window.location.pathname !== '/signin') {
            window.location.href = '/signin';
        } else {
            setTimeout(() => {
                this._isHandlingAuthFailure = false;
            }, 1000);
        }
    },

    /**
     * Clear token cache
     */
    clearTokenCache() {
        try {
            oktaAuth.tokenManager.clear();
        } catch (error) {
            console.warn('Error clearing token cache:', error);
        }
    },

    /**
     * Get Access Token
     */
    async getValidToken() {
        try {
            const token = await oktaAuth.tokenManager.get('accessToken');
            if (token) {
                this.updateActivity();
                return token.accessToken;
            }
        } catch (error) {
            console.error('❌ Error getting valid token:', error);
            this.handleAuthFailure('Token renewal failed - access token invalid');
        }
        return null;
    },

    /**
     * Get user info from token
     */
    async getUserInfo() {
        const accessTokenObj = await oktaAuth.tokenManager.get('accessToken');
        if (accessTokenObj) {
            return await oktaAuth.token.getUserInfo(accessTokenObj);
        }
        return null;
    },

    /**
     * Get user groups from access token claims
     */
    async getUserGroups() {
        const accessTokenObj = await oktaAuth.tokenManager.get('accessToken');
        if (accessTokenObj && accessTokenObj.claims) {
            return accessTokenObj.claims.groups || [];
        }
        return [];
    },

    /**
     * Force Login
     */
    async login(fromPath = null) {
        if (fromPath) {
            sessionStorage.setItem('okta_redirect_path', fromPath);
        }
        await oktaAuth.signInWithRedirect();
    },

    /**
     * Logout
     */
    async logout() {
        console.log('👋 Logging out...');
        this._stopIdleMonitoring();
        this._stopTokenExpirationListener();
        
        await oktaAuth.signOut({ 
            postLogoutRedirectUri: isLocal ? "http://localhost:5002" : window.location.origin
        });
    },

    /**
     * Handle the Callback
     */
    async handleLoginCallback() {
        try {
            await oktaAuth.handleLoginRedirect();
            this._startTokenExpirationListener();
            
            const savedPath = sessionStorage.getItem('okta_redirect_path');
            sessionStorage.removeItem('okta_redirect_path');
            return savedPath || '/';
        } catch (error) {
            console.error('❌ Callback error:', error);
            throw error;
        }
    },

    // ==================== IDLE MANAGEMENT ====================
    
    updateActivity() {
        this._lastActivity = Date.now();
    },

    _startIdleMonitoring() {
        ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(evt => 
            window.addEventListener(evt, () => this.updateActivity(), { passive: true })
        );

        this._idleInterval = setInterval(() => {
            if (Date.now() - this._lastActivity > this._maxIdleTime) {
                console.log('💤 Idle timeout reached');
                this.logout();
            }
        }, 60000);
    },

    _stopIdleMonitoring() {
        if (this._idleInterval) clearInterval(this._idleInterval);
    },
};

export default tokenManagerService;
export { oktaAuth };

